import {CommentsCategory} from '@domain/comments'
import type {BaseSchedule} from '@domain/schedule'
import type {AssetSnapshot} from '@hconnect/common/types'
import {dataTestId} from '@hconnect/uikit'
import {Stack} from '@mui/material'
import {CommentSidebarButton, useCommentsPermissions} from '@widgets/planning-comments'
import {Moment} from 'moment-timezone'
import React from 'react'

import {PlannerFeature, usePlannerFeature} from '../../../../hooks'

import {CalculateCostAvoidanceButton} from './CalculateCostAvoidanceButton'
import {ExcelDownload} from './ExcelDownload'
import {RemoveAutofillItemsButton} from './RemoveAutofillItemsButton'
import {SendBackupButton} from './SendBackupButton'

interface PlanningActionsContentProps {
  toggleCommentsSidebar?: () => void
  isPlanningCardCollapsed: boolean
  schedule: BaseSchedule | undefined
  assets: AssetSnapshot[] | undefined
  startOfChart: Moment
  endOfChart: Moment
}

export const PlanningActionsContent: React.FC<PlanningActionsContentProps> = ({
  toggleCommentsSidebar,
  isPlanningCardCollapsed,
  schedule,
  assets,
  startOfChart,
  endOfChart
}) => {
  const isCostAvoidanceAvailable = usePlannerFeature(PlannerFeature.CostAvoidance)
  const isSubmitAvailable = usePlannerFeature(PlannerFeature.ElectricityPurchasing)
  const areCommentsEnabled = usePlannerFeature(PlannerFeature.PlanningComments)
  const {canViewComments: canViewAssetComments} = useCommentsPermissions(CommentsCategory.Assets)
  const {canViewComments: canViewAssetOperationTimesComments} = useCommentsPermissions(
    CommentsCategory.AssetOperationTimes
  )

  const canViewComments = canViewAssetComments || canViewAssetOperationTimesComments
  const shouldShowSendBackupButton = !isSubmitAvailable
  const shouldShowCalculateCostAvoidanceButton = !isSubmitAvailable && isCostAvoidanceAvailable

  const shouldShowCommentSidebarButton =
    areCommentsEnabled && canViewComments && toggleCommentsSidebar && !isPlanningCardCollapsed
  return (
    <>
      <Stack
        spacing={1.5}
        sx={{display: 'flex', alignItems: 'center'}}
        direction="row"
        {...dataTestId('planning_actions')}
      >
        <RemoveAutofillItemsButton />
        <ExcelDownload />
        {shouldShowSendBackupButton && <SendBackupButton />}
        {shouldShowCalculateCostAvoidanceButton && <CalculateCostAvoidanceButton />}
        {shouldShowCommentSidebarButton && (
          <CommentSidebarButton
            toggleCommentSidebar={toggleCommentsSidebar}
            schedule={schedule}
            assets={assets}
            startOfChart={startOfChart}
            endOfChart={endOfChart}
          />
        )}
      </Stack>
    </>
  )
}
